/**
 * Reglas PURAS de la página del expediente de Studio (`/consent/:id`). Viven aparte de la pantalla
 * para probarlas sin navegador.
 *
 * Interfaz con la app de la clienta FIJADA el 8/oct/2026 (`planes/PLAN-CONTRATO-MICROBLADING.md`,
 * «Puerta de la app de la clienta»; ya publicada en la 1.0.39): la app abre
 * `app.kabandu.com/consent/<id>?app=1` en un WebView con el canal `KabanduApp` y le presta la sesión
 * a la página. Cambiar el protocolo de abajo exige otro release de tienda.
 */
import type { HistorialClinico, RespuestaCondicion } from '@api/Consents/Consent';

/**
 * Cómo se abrió la página:
 * - `app`: dentro de la app de la clienta (con `?app=1` Y el canal presente): su contrato.
 * - `personal`: el QR que el Punto de Venta le muestra a la clienta (`?staff=`, link de 2 h, y
 *   `&order=` del pedido): estación de firma en su teléfono o la tablet.
 * - `puente`: cualquier otro caso. Fuera de la app no se firma: se invita a descargarla.
 */
export type ModoPagina =
    | { modo: 'app' }
    | { modo: 'personal'; staffToken: string; orderId?: string }
    | { modo: 'puente' };

export const detectarModo = (search: string, tieneCanal: boolean): ModoPagina => {
    const params = new URLSearchParams(search);
    const staffToken = (params.get('staff') || '').trim();
    const orderId = (params.get('order') || '').trim();
    if (staffToken) return { modo: 'personal', staffToken, ...(orderId ? { orderId } : {}) };
    if (params.get('app') === '1' && tieneCanal) return { modo: 'app' };
    return { modo: 'puente' };
};

/* ------------------------------------------------------------------------------------------------
 * Canal con la app: página → app `{"type":"getIdToken","requestId":"…"}`; la app responde llamando
 * `window.kabanduAppReceive({type:'idToken'|'error', requestId, idToken?, uid?, message?})`.
 * ---------------------------------------------------------------------------------------------- */

export type RespuestaApp =
    | { requestId: string; ok: true; idToken: string }
    | { requestId: string; ok: false; mensaje: string };

/** Lee lo que la app manda por `kabanduAppReceive` (objeto, o texto JSON por si acaso). */
export const leerRespuestaApp = (raw: unknown): RespuestaApp | undefined => {
    let msg = raw;
    if (typeof msg === 'string') {
        try {
            msg = JSON.parse(msg);
        } catch {
            return undefined;
        }
    }
    if (!msg || typeof msg !== 'object') return undefined;
    const { type, requestId, idToken, message } = msg as Record<string, unknown>;
    if (typeof requestId !== 'string' || !requestId) return undefined;
    if (type === 'idToken' && typeof idToken === 'string' && idToken) {
        return { requestId, ok: true, idToken };
    }
    if (type === 'error') {
        return {
            requestId,
            ok: false,
            mensaje: typeof message === 'string' && message ? message : 'No pudimos confirmar tu sesión.'
        };
    }
    return undefined;
};

/* ------------------------------------------------------------------------------------------------
 * Cuestionario clínico
 * ---------------------------------------------------------------------------------------------- */

export type CondicionesBorrador = Record<string, RespuestaCondicion | undefined>;

export interface HistorialBorrador {
    domicilio: string;
    colonia: string;
    actividad: string;
    condiciones: CondicionesBorrador;
    medicamento: string;
    comentarios: string;
}

/** El formulario arranca con lo guardado; sin historial, todo vacío y sin respuestas marcadas. */
export const borradorDesde = (historial: HistorialClinico | null, claves: string[]): HistorialBorrador => ({
    domicilio: historial?.domicilio ?? '',
    colonia: historial?.colonia ?? '',
    actividad: historial?.actividad ?? '',
    condiciones: Object.fromEntries(claves.map((c) => [c, historial?.condiciones?.[c]])),
    medicamento: historial?.medicamento ?? '',
    comentarios: historial?.comentarios ?? ''
});

/** Las condiciones sin responder: el server exige las 14 (sí, no o controlada). */
export const condicionesSinResponder = (borrador: HistorialBorrador, claves: string[]): string[] =>
    claves.filter((c) => !borrador.condiciones[c]);

/** «Marcar el resto en No»: solo toca las que faltan, nunca cambia una respuesta dada. */
export const restoEnNo = (borrador: HistorialBorrador, claves: string[]): HistorialBorrador => ({
    ...borrador,
    condiciones: Object.fromEntries(claves.map((c) => [c, borrador.condiciones[c] ?? 'no']))
});

/** Lo que se manda a `PUT /consents/:id/clinical` (el server recorta y valida otra vez). */
export const historialParaEnviar = (borrador: HistorialBorrador): HistorialClinico => ({
    domicilio: borrador.domicilio.trim(),
    colonia: borrador.colonia.trim(),
    actividad: borrador.actividad.trim(),
    condiciones: Object.fromEntries(
        Object.entries(borrador.condiciones).filter((e): e is [string, RespuestaCondicion] => !!e[1])
    ),
    medicamento: borrador.medicamento.trim(),
    comentarios: borrador.comentarios.trim()
});

export const ETIQUETA_RESPUESTA: Record<RespuestaCondicion, string> = {
    si: 'Sí',
    no: 'No',
    controlada: 'Controlada'
};

/**
 * ¿El renglón es del día? Se firma al inicio de la cita, así que el formulario sale abierto solo
 * para la cita de HOY (en la hora del dispositivo, que está en la sucursal) o una venta directa
 * sin cita. Los demás se pueden firmar igual, pero tras un toque que avisa de qué fecha son.
 */
export const esRenglonDeHoy = (fechaIso: string | null, ahora: Date = new Date()): boolean => {
    if (!fechaIso) return true;
    const fecha = new Date(fechaIso);
    if (Number.isNaN(fecha.getTime())) return true;
    return (
        fecha.getFullYear() === ahora.getFullYear() &&
        fecha.getMonth() === ahora.getMonth() &&
        fecha.getDate() === ahora.getDate()
    );
};

/* ------------------------------------------------------------------------------------------------
 * Formato
 * ---------------------------------------------------------------------------------------------- */

export const pesos = (monto: number): string =>
    `$${monto.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** «14 de octubre de 2026, 11:00 a. m.» en la hora del teléfono (como el ticket y las apps). */
export const fechaHora = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const fecha = new Date(iso);
    if (Number.isNaN(fecha.getTime())) return '';
    return fecha.toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });
};

/** Cierra una frase con punto sin duplicarlo («… 1:53 p.m.» ya trae el suyo). */
export const conPunto = (texto: string): string => (/[.!?]$/.test(texto.trim()) ? texto.trim() : `${texto.trim()}.`);

export const fechaCorta = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const fecha = new Date(iso);
    if (Number.isNaN(fecha.getTime())) return '';
    return fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
};
